import { Fragment, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  CheckCircleOutline,
  ErrorOutline,
  MailOutline,
  Send,
  ReportProblemOutlined,
} from '@mui/icons-material';

import { logout } from '../../../actions/auth';
import { withRouter } from '../../hooks/withRouter';
import NewHomework from './NewHomework';

import 'filepond/dist/filepond.min.css';
import { registerPlugin } from 'react-filepond';

import FilePondPluginGetFile from 'filepond-plugin-get-file';
import 'filepond-plugin-get-file/dist/filepond-plugin-get-file.min.css';
import FilePondPluginMediaPreview from 'filepond-plugin-media-preview';
import FilePondPluginPdfPreview from 'filepond-plugin-pdf-preview';

import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';

import { getHomeworks } from '../../../api/homework';
import HomeWorkItem from './HomeWorkItem';
import HwReplyItem from './HwReplyItem';

import './Homeworks.scss';

registerPlugin(
  FilePondPluginMediaPreview,
  FilePondPluginPdfPreview,
  FilePondPluginGetFile,
  FilePondPluginImagePreview,
);

const getHomeworkStatus = (status) => {
  switch (Number(status)) {
    case 10:
      return {
        label: 'Mail küldés hiba',
        icon: <ErrorOutline />,
        color: 'error',
      };

    case 20:
      return {
        label: 'Mail küldés folyamatban',
        icon: <MailOutline />,
        color: 'warning',
      };

    case 30:
      return {
        label: 'Mail küldés sikeres',
        icon: <Send />,
        color: 'success',
      };

    case 50:
      return {
        label: 'Feladat visszaküldve',
        icon: <CheckCircleOutline />,
        color: 'success',
      };

    default:
      return {
        label: '!! Hiba !!',
        icon: <ReportProblemOutlined />,
        color: 'error',
      };
  }
};

const HomeworkLegend = () => {
  return (
    <Stack spacing={1}>
      <Alert severity='error' variant='outlined'>
        Nem sikerült létrehozni a mail küldést – kattints az ikonra és próbáld
        újra.
      </Alert>

      <Alert severity='warning' variant='outlined'>
        E-mail küldés előjegyezve, 15 percen belül elküldésre kerül.
      </Alert>

      <Alert severity='success' variant='outlined'>
        E-mail küldés megtörtént.
      </Alert>

      <Alert severity='info' variant='outlined'>
        A házi feladatot visszaküldte a diákod.
      </Alert>

      <Alert severity='error' variant='outlined'>
        Rendszerhiba történt, szólj a főnöknek vagy próbáld újra az ikonra
        kattintva.
      </Alert>
    </Stack>
  );
};

const FormCreateHomeWork = ({ logout, auth }) => {
  const [homeWorks, setHomeWorks] = useState([]);
  const [displayAll, setDisplayAll] = useState(false);
  const [displayReplied, setDisplayReplied] = useState(false);
  const [diakNev, setDiakNev] = useState('');
  const [loading, setLoading] = useState(false);

  const canDisplayAll = auth.user.permissions.includes('homework.display.all');

  useEffect(() => {
    if (!auth.isAuthenticated) {
      logout();
      return;
    }

    async function apiCall() {
      setLoading(true);

      try {
        const apiResponse = await getHomeworks({
          displayAll,
          displayReplied,
        });

        const data = Array.isArray(apiResponse?.data) ? apiResponse.data : [];
        setHomeWorks(data);
      } catch (error) {
        console.error('Házi feladatok betöltési hiba:', error);
        setHomeWorks([]);
      } finally {
        setLoading(false);
      }
    }

    apiCall();
  }, [auth.isAuthenticated, displayAll, displayReplied, logout]);

  const filteredHomeWorks = useMemo(() => {
    const needle = diakNev.trim().toLowerCase();

    if (!needle) {
      return homeWorks;
    }

    return homeWorks.filter((hw) =>
      String(hw.diak ?? '')
        .toLowerCase()
        .includes(needle),
    );
  }, [homeWorks, diakNev]);

  function onStatusChange(hw) {
    console.log('Clicked', hw.id);

    // Ide jöhet később például:
    // retryHomeworkMail(hw.id)
    // majd újratöltés
  }

  return (
    <Fragment>
      <Box sx={{ p: 2 }}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={3}
              alignItems='flex-start'
              sx={{ mb: 3 }}
            >
              <Box sx={{ flex: 1, width: '100%' }}>
                <Typography variant='h6' fontWeight={700} gutterBottom>
                  Házi feladatok megtekintése
                </Typography>

                <HomeworkLegend />

                <Divider sx={{ my: 2 }} />

                <Stack spacing={1.5}>
                  {canDisplayAll && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={displayAll}
                          onChange={(e) => setDisplayAll(e.target.checked)}
                        />
                      }
                      label='Összes oktató kiküldött feladatainak megjelenítése'
                    />
                  )}

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={displayReplied}
                        onChange={(e) => setDisplayReplied(e.target.checked)}
                      />
                    }
                    label='Visszaküldött feladatok megjelenítése'
                  />

                  <TextField
                    label='Diák keresése'
                    value={diakNev}
                    onChange={(e) => setDiakNev(e.target.value)}
                    size='small'
                    fullWidth
                  />
                </Stack>
              </Box>

              <Box sx={{ width: { xs: '100%', lg: 420 } }}>
                <Card variant='outlined' sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography
                      variant='subtitle1'
                      fontWeight={700}
                      gutterBottom
                    >
                      Új házi feladat
                    </Typography>

                    <NewHomework />
                  </CardContent>
                </Card>
              </Box>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              {loading && (
                <Typography color='text.secondary'>Betöltés...</Typography>
              )}

              {!loading && filteredHomeWorks.length === 0 && (
                <Typography color='text.secondary'>
                  Nincs megjeleníthető házi feladat.
                </Typography>
              )}

              {!loading &&
                filteredHomeWorks.map((hw) => {
                  const status = getHomeworkStatus(hw.homework_status);

                  return (
                    <Card
                      key={hw.id}
                      variant='outlined'
                      sx={{
                        borderRadius: 3,
                        overflow: 'hidden',
                      }}
                    >
                      <CardContent>
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={2}
                          alignItems={{ xs: 'stretch', md: 'flex-start' }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <HomeWorkItem
                              id={hw.id}
                              file={hw.file}
                              title={hw.homework_title}
                              description={hw.homework_description}
                              diak_id={hw.id_diak}
                              createad_at={hw.created_at}
                              status={hw.homework_status}
                              deadline={hw.homework_deadline}
                              jelenlet_id={hw.jelenlet_id}
                            />

                            {Array.isArray(hw.replies) &&
                              hw.replies.length > 0 && (
                                <Box sx={{ mt: 2, pl: 2 }}>
                                  <Typography
                                    variant='subtitle2'
                                    fontWeight={700}
                                    gutterBottom
                                  >
                                    Visszaküldött fájlok
                                  </Typography>

                                  <Stack spacing={1}>
                                    {hw.replies.map((reply) => (
                                      <Box key={reply.id}>
                                        <HwReplyItem
                                          id={reply.id}
                                          file={reply.file}
                                        />
                                      </Box>
                                    ))}
                                  </Stack>
                                </Box>
                              )}
                          </Box>

                          <Box
                            sx={{
                              minWidth: { xs: '100%', md: 190 },
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: { xs: 'stretch', md: 'center' },
                              gap: 1,
                            }}
                          >
                            <Button
                              variant='outlined'
                              color={status.color}
                              startIcon={status.icon}
                              onClick={() => onStatusChange(hw)}
                              fullWidth
                            >
                              {status.label}
                            </Button>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Fragment>
  );
};

FormCreateHomeWork.propTypes = {
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, { logout })(
  withRouter(FormCreateHomeWork),
);
